/**
 * One shared tooltip, rendered on <body> in viewport coordinates so it can
 * never be clipped by a scrolling panel. Shows on hover or keyboard focus,
 * prefers sitting above its target, and flips below when there is no room.
 */

const GAP = 6;
const EDGE = 8;

export interface TooltipLine {
  text: string;
  muted?: boolean;
  /** A price, in the same yellow the shop uses. */
  cost?: boolean;
}

/** An icon shown beside the lines, optionally with a corner badge. */
export interface TooltipIcon {
  src: string;
  badge?: string;
  /** Drawn faded, for something not yet earned. */
  dim?: boolean;
}

export interface TooltipContent {
  icon?: TooltipIcon;
  lines: readonly TooltipLine[];
}

let tip: HTMLElement | null = null;
let current: HTMLElement | null = null;

function ensure(): HTMLElement {
  if (tip) return tip;
  tip = document.createElement('div');
  tip.className = 'tooltip';
  tip.setAttribute('role', 'tooltip');
  tip.hidden = true;
  document.body.append(tip);
  // Any scroll invalidates the position; hide rather than chase it.
  document.addEventListener('scroll', hide, true);
  window.addEventListener('resize', hide);
  return tip;
}

function hide(): void {
  if (!tip) return;
  tip.hidden = true;
  current = null;
}

function show(target: HTMLElement, content: TooltipContent): void {
  const el = ensure();
  const body = document.createElement('div');
  body.className = 'tooltip__body';
  body.append(
    ...content.lines.map((line) => {
      const p = document.createElement('div');
      p.className = 'tooltip__line';
      if (line.muted) p.classList.add('tooltip__line--muted');
      if (line.cost) p.classList.add('tooltip__line--cost');
      p.textContent = line.text;
      return p;
    }),
  );
  if (content.icon) {
    const box = document.createElement('span');
    box.className = content.icon.dim ? 'tooltip__icon tooltip__icon--dim' : 'tooltip__icon';
    const img = document.createElement('img');
    img.alt = '';
    img.src = content.icon.src;
    box.append(img);
    if (content.icon.badge) {
      const badge = document.createElement('span');
      badge.className = 'tooltip__badge';
      badge.textContent = content.icon.badge;
      box.append(badge);
    }
    el.replaceChildren(box, body);
  } else {
    el.replaceChildren(body);
  }
  current = target;

  // Measure while invisible, then place.
  el.style.visibility = 'hidden';
  el.hidden = false;
  const rect = target.getBoundingClientRect();
  const w = el.offsetWidth;
  const h = el.offsetHeight;

  let top = rect.top - h - GAP;
  if (top < EDGE) top = rect.bottom + GAP;
  const maxLeft = window.innerWidth - w - EDGE;
  const left = Math.max(EDGE, Math.min(rect.left, maxLeft));

  el.style.top = `${Math.round(top)}px`;
  el.style.left = `${Math.round(left)}px`;
  el.style.visibility = '';
}

/** Attaches a tooltip whose content is computed each time it opens: plain lines, or lines with an icon. */
export function bindTooltip(
  target: HTMLElement,
  content: () => readonly TooltipLine[] | TooltipContent,
): void {
  const open = (): void => {
    const c = content();
    const full = 'lines' in c ? c : { lines: c };
    // Nothing to say: no empty box.
    if (full.lines.length === 0) return;
    show(target, full);
  };
  const close = (): void => {
    if (current === target) hide();
  };
  target.addEventListener('pointerenter', open);
  target.addEventListener('pointerleave', close);
  target.addEventListener('focus', open);
  target.addEventListener('blur', close);
  // A tap on touch devices fires pointerenter without a leave; a second tap closes.
  target.addEventListener('pointerdown', (ev: PointerEvent) => {
    if (ev.pointerType === 'touch' && current === target) hide();
  });
}
